// Shared primitives — import from here, never redefine elsewhere.

export function esc(s: unknown): string {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (isNaN(diff) || diff < 0) return String(dateStr);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function mdInline(t: string): string {
  return (t ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g,     '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="font-family:var(--mono);font-size:0.9em;background:var(--bg3);padding:1px 4px;border-radius:2px;">$1</code>');
}

export function renderMarkdown(md: string, tableClass?: string): string {
  if (!md) return '';
  const tblOpen = tableClass ? `<table class="${tableClass}">` : '<table>';
  const lines = md.split('\n');
  let html = '', inList = false, inTable = false;
  const tableRows: (string | null)[] = [];

  const flush = () => {
    if (inList) { html += '</ul>'; inList = false; }
    if (inTable) {
      let t = tblOpen;
      tableRows.forEach((r, i) => {
        if (i === 0)      t += `<thead>${r}</thead>`;
        else if (i === 1) { /* separator row — skip */ }
        else              { if (i === 2) t += '<tbody>'; t += r ?? ''; }
      });
      if (tableRows.length > 2) t += '</tbody>';
      html += t + '</table>';
      inTable = false; tableRows.length = 0;
    }
  };

  for (const line of lines) {
    if (/^\|.+\|/.test(line)) {
      if (inList) { html += '</ul>'; inList = false; }
      if (/^\|[\s\-:|]+\|$/.test(line)) { tableRows.push(null); inTable = true; continue; }
      const cells = line.split('|').filter((_, i, a) => i > 0 && i < a.length - 1).map(c => c.trim());
      const tag = !inTable || tableRows.length === 0 ? 'th' : 'td';
      tableRows.push('<tr>' + cells.map(c => `<${tag}>${mdInline(c)}</${tag}>`).join('') + '</tr>');
      inTable = true; continue;
    }
    if (inTable) flush();
    if (/^#### /.test(line)) { flush(); html += `<h4>${mdInline(line.slice(5))}</h4>`; continue; }
    if (/^### /.test(line))  { flush(); html += `<h3>${mdInline(line.slice(4))}</h3>`; continue; }
    if (/^## /.test(line))   { flush(); html += `<h2>${mdInline(line.slice(3))}</h2>`; continue; }
    if (/^# /.test(line))    { flush(); html += `<h1>${mdInline(line.slice(2))}</h1>`; continue; }
    if (/^---+$/.test(line.trim())) { flush(); html += '<hr>'; continue; }
    if (/^> /.test(line))    { flush(); html += `<blockquote>${mdInline(line.slice(2))}</blockquote>`; continue; }
    if (/^[-•*] /.test(line)) {
      if (!inList) { flush(); html += '<ul>'; inList = true; }
      html += `<li>${mdInline(line.replace(/^[-•*] /, ''))}</li>`; continue;
    }
    flush();
    if (line.trim()) html += `<p>${mdInline(line)}</p>`;
  }
  flush();
  return html;
}

export function filterTable(tableId: string, query: string): void {
  const q = query.toLowerCase();
  document.querySelectorAll<HTMLElement>(`#${tableId} tbody tr`).forEach(row => {
    row.style.display = row.textContent?.toLowerCase().includes(q) ? '' : 'none';
  });
}

export function setTableState(
  tbodyId: string,
  wrapId:  string | null,
  emptyId: string | null,
  rows:    unknown[],
  buildFn: (row: any) => string,
): boolean {
  const has = rows.length > 0;
  const tbody = document.getElementById(tbodyId);
  if (tbody) tbody.innerHTML = has ? rows.map(buildFn).join('') : '';
  if (wrapId)  { const el = document.getElementById(wrapId);  if (el) el.style.display = has ? '' : 'none'; }
  if (emptyId) { const el = document.getElementById(emptyId); if (el) el.style.display = has ? 'none' : ''; }
  return has;
}
